import React, { useState, useEffect, useRef } from 'react';

const CompanyOverview = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('history');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const keyCapabilities = [
    { name: 'Product Development', icon: '/img/icon/product.jpg', description: 'High-quality OEM solutions', stat: '500+', statLabel: 'Products Developed' },
    { name: 'Manufacturing', icon: '/img/icon/manufact.jpg', description: 'Advanced production facilities', stat: '50K+', statLabel: 'Sq. Ft. Facility' },
    { name: 'Retail Support', icon: '/img/icon/retail.jpg', description: 'Dedicated retail partnership', stat: '100%', statLabel: 'Satisfaction Rate' },
  ];

  const productSegments = [
    { name: 'Statues', icon: '/img/icon/statues.jpg', description: 'Decorative outdoor statues' },
    { name: 'Birdbaths', icon: '/img/icon/birdbaths.jpg', description: 'Elegant birdbath designs' },
    { name: 'Accessories', icon: '/img/icon/accessories.jpg', description: 'Matching accessories' },
  ];

  const teamMembers = [
    { name: 'HU LINKAI', role: 'Senior Manager', image: '/img/team/hulinkai.jpg', email: 'maosente.cambodia@gmail.com' },
    { name: 'LIYA', role: 'Senior Sales Manager', image: '/img/team/liya.jpg', email: 'maosente.cambodia@gmail.com' },
    { name: 'XIAO KUNPENG', role: 'Painting Manager', image: '/img/team/xiaokunpeng.jpg', email: 'maosente.cambodia@gmail.com' },
    { name: 'XIAO YUNCHENG', role: 'Packing Manager', image: '/img/team/xiaoyuncheng.jpg', email: 'maosente.cambodia@gmail.com' },
    { name: 'HU FEI', role: 'Operations Manager', image: '/img/team/hufei.jpg', email: 'maosente.cambodia@gmail.com' },
  ];

  return (
    <section id="company" ref={sectionRef} className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header - Clean & Simple */}
        <div className="text-center mb-16">
          <span className="text-sm font-semibold text-[#215f9a] uppercase tracking-wider">Who We Are</span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2 mb-4">
            Company Overview
          </h2>
          <div className="w-16 h-1 bg-[#215f9a] mx-auto mb-6"></div>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            We provide high-quality outdoor décor and lighting OEM solutions to retailers across the US and Europe, 
            combining innovation with exceptional craftsmanship.
          </p>
        </div>

        {/* Tab Navigation - Minimal */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-1 border-b border-gray-200">
            {[
              { id: 'history', label: 'Our History' },
              { id: 'vision', label: 'Vision & Mission' },
              { id: 'facts', label: 'Key Facts' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 text-sm font-medium transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'text-[#215f9a] border-b-2 border-[#215f9a]'
                    : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="mt-8">
            {activeTab === 'history' && (
              <div className="grid md:grid-cols-2 gap-10 items-center">
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Est. 2009</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    Established in 2009 in China, with our Cambodia factory established in 2025, leveraging 
                    Mr. HU's 16-year experience with manufacturing and production deep industry expertise.
                  </p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                      <div className="text-2xl font-bold text-[#215f9a]">2009</div>
                      <div className="text-xs text-gray-500">China Foundation</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                      <div className="text-2xl font-bold text-[#215f9a]">2025</div>
                      <div className="text-xs text-gray-500">Cambodia Expansion</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                      <div className="text-2xl font-bold text-[#215f9a]">16+</div>
                      <div className="text-xs text-gray-500">Years Experience</div>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                  <p className="text-gray-600 italic mb-4">
                    "From a single facility in China to a global manufacturing presence, 
                    our journey is defined by continuous growth and unwavering commitment to quality."
                  </p>
                  <div className="pt-4 border-t border-gray-100">
                    <p className="font-semibold text-gray-900">HU CHONGSHOU</p>
                    <p className="text-sm text-gray-500">Founder & CEO</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'vision' && (
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#215f9a]/10 flex items-center justify-center">
                    <svg className="w-7 h-7 text-[#215f9a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Innovation</h4>
                  <p className="text-gray-500 text-sm">Drive innovation through continuous R&D</p>
                </div>
                <div className="text-center p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#215f9a]/10 flex items-center justify-center">
                    <svg className="w-7 h-7 text-[#215f9a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Craftsmanship</h4>
                  <p className="text-gray-500 text-sm">Uphold exceptional craftsmanship in every product</p>
                </div>
                <div className="text-center p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#215f9a]/10 flex items-center justify-center">
                    <svg className="w-7 h-7 text-[#215f9a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Partnership</h4>
                  <p className="text-gray-500 text-sm">Be the trusted partner for retailers worldwide</p>
                </div>
              </div>
            )}

            {activeTab === 'facts' && (
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="space-y-4">
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Management</span>
                      <span className="font-semibold text-gray-900">HU CHONGSHOU</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">2024 Sales</span>
                      <span className="font-semibold text-gray-900">~ US$ 0.8 million</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Corporate Headquarters</span>
                      <span className="font-semibold text-gray-900">China</span>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="space-y-4">
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Ports</span>
                      <span className="font-semibold text-gray-900">Yantian, Ho Chi Minh City</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Production Lead Time</span>
                      <span className="font-semibold text-gray-900">75 Days / 60 Days</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block mb-2">Key Customers</span>
                      <div className="flex flex-wrap gap-2">
                        {['Lowe\'s', 'Walmart', 'ALDI', 'Home Depot'].map((customer, idx) => (
                          <span key={idx} className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                            {customer}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Key Capabilities - Clean Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-20">
          {keyCapabilities.map((cap, idx) => (
            <div key={idx} className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <div className="w-14 h-14 rounded-lg bg-[#215f9a] flex items-center justify-center mb-4">
                <img 
                  src={cap.icon} 
                  alt={cap.name}
                  className="w-8 h-8 object-contain brightness-0 invert"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<span class="text-white text-xl font-bold">' + cap.name.charAt(0) + '</span>';
                  }}
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{cap.name}</h3>
              <p className="text-gray-500 text-sm mb-3">{cap.description}</p>
              <div className="pt-3 border-t border-gray-100">
                <div className="text-2xl font-bold text-[#215f9a]">{cap.stat}</div>
                <div className="text-xs text-gray-400">{cap.statLabel}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Factories Section - Split Layout */}
        <div className="grid md:grid-cols-2 gap-6 mb-20">
          <div className="bg-[#215f9a] rounded-lg p-8 text-white">
            <svg className="w-10 h-10 mb-4 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="text-2xl font-bold mb-3">Global Manufacturing Network</h3>
            <p className="text-white/80 leading-relaxed mb-4">
              Diversified manufacturing network in Dongguan, China, and Southeast Asia (Cambodia). 
              Strategically positioned to adapt to the evolving global trade landscape.
            </p>
            <div className="flex gap-3">
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm">China</span>
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Cambodia</span>
            </div>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Production Capabilities</h3>
            <div className="space-y-4">
              {[
                { label: 'Quality Control', value: 98 },
                { label: 'On-Time Delivery', value: 95 },
                { label: 'Client Retention', value: 92 }
              ].map((item, idx) => (
                <div key={idx}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">{item.label}</span>
                    <span className="font-semibold text-gray-900">{item.value}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-full rounded-full bg-[#215f9a] transition-all duration-1000"
                      style={{ width: `${isVisible ? item.value : 0}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Team Section */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Leadership Team</h3>
            <p className="text-gray-500">Meet the experts driving our success</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {teamMembers.map((member, idx) => (
              <div key={idx} className="text-center group">
                <div className="w-32 h-32 mx-auto mb-3 rounded-full overflow-hidden shadow-md">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    onError={(e) => {
                      e.target.src = 'https://randomuser.me/api/portraits/men/default.jpg';
                    }}
                  />
                </div>
                <h4 className="font-semibold text-gray-900">{member.name}</h4>
                <p className="text-sm text-gray-500 mt-1">{member.role}</p>
                <a href={`mailto:${member.email}`} className="text-xs text-[#215f9a] hover:underline mt-2 inline-block">
                  Contact
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Product Segments */}
        <div>
          <div className="text-center mb-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Product Segments</h3>
            <p className="text-gray-500">Discover our premium product categories</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {productSegments.map((segment, idx) => (
              <div key={idx} className="group bg-white rounded-lg p-6 text-center shadow-sm hover:shadow-md transition-all border border-gray-100">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#215f9a]/10 flex items-center justify-center group-hover:bg-[#215f9a] transition-colors duration-300">
                  <img 
                    src={segment.icon} 
                    alt={segment.name}
                    className="w-10 h-10 object-contain group-hover:brightness-0 group-hover:invert transition-all duration-300"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<span class="text-2xl font-bold text-[#215f9a] group-hover:text-white">' + segment.name.charAt(0) + '</span>';
                    }}
                  />
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">{segment.name}</h4>
                <p className="text-gray-500 text-sm mb-4">{segment.description}</p>
                <button className="text-[#215f9a] text-sm font-medium hover:underline inline-flex items-center gap-1">
                  Learn More 
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyOverview;